import StageCellEditor from './StageCellEditor';
import TagCellEditor from '../tags/TagCellEditor';
import { PipelinePinIcon } from '../../../assets/pipeline/PipelinePinIcon';
import { getStrategyLabel, getOwnerInitials } from '../utils.js';

export const getPipelineColumns = ({ activeStage, onStageUpdate, allTags, onToggleTag, onManageTags, onNavigate, onPinToggle, onSaveOk, onSaveFailed, user }) => [
  {
    field: "property",
    headerName: "Property",
    flex: 2,
    sortable: false,

    renderCell: (params) => {
      // Non-blank rather than truthy: `' '` is truthy, and a whitespace-only street would draw both
      // its own run and the separator. `addressDisplay` upstream in `mapPropertyToGridRow` filters
      // the same way, so the `title` and the visible line cannot disagree.
      const hasStreet = Boolean(params.row.streetDisplay && String(params.row.streetDisplay).trim())
      return (
        // pr-[12px]: the grid-wide ".MuiDataGrid-cell { padding: 0 !important }" rule (PipelineGrid.jsx)
        // leaves no gap of its own, so a truncated line would otherwise butt straight into Stage's
        // pill with nothing between them. The gutter lives here, not on the shared rule, because
        // every other column depends on that rule staying at 0.
        <div
          className="flex flex-col justify-center leading-[1.2] cursor-pointer py-[16px] pr-[12px] w-[100%] group"
          onClick={() => onNavigate?.(params.row.id)}
          title={params.row.addressDisplay}
        >
          {/* truncate: text-overflow only applies to the element that actually overflows. These
              spans are block (flex-col children), so the ellipsis has to sit on the span itself,
              not the row div or the MUI cell wrapper. */}
          {/* pipeline.md §1: row 1 (address) is 14px #17270D, row 2 (summary) is 13px #4F5F4A —
              both measured against sheet-row-default/Frame 176_2 in Figma, not inherited from the
              pre-mr-05 13px/semibold and 12px/unselectedGray these carried.

              Comment Set #1, Pipeline 1 — "Bold the address under Property column. City, state, and
              zip are excluded from bolding" — with ARV 7.7 saying where they go instead: "City,
              state, and zip should be on Row 1 of Property". Both land here, because in the export
              they are one line. `sheet-row-default/Frame 176_2` draws `10425 Old Mill Creek Rd` at
              14/bold #17270D and `Denver, CO 80202` at 13/300 #4F5F4A on the SAME baseline, y
              1324.4, with the spec row alone on y 1340.4 below them.

              Which is why the weight waited for this: until the split, row 1 was one string holding
              both halves, and bolding it would have bolded the half he excluded.

              The truncation lives on this outer span, not on either run. Both children are inline,
              so the line overflows as one and the ellipsis belongs to the box that clips it. */}
          {/* The hover colour is on this outer span as well as on both runs, and it is not
              redundant: on a truncated row the ellipsis is generated by THIS box, so with the colour
              only on the children the text turned green under the pointer while the "…" stayed
              `#17270D`. One character, and only on rows long enough to clip. */}
          <span className="text-[14px] text-brand-dark truncate group-hover:text-brand-green transition-colors">
            {hasStreet ? (
              <span className="font-bold group-hover:text-brand-green transition-colors">
                {params.row.streetDisplay}
              </span>
            ) : null}
            {params.row.cityStateZip ? (
              <>
                {/* Conditional on BOTH runs, and the street half is tested for non-blank rather than
                    for truthy — `' '` is truthy, and that is the one state of this pair that paints
                    a visible indent (6.81px measured).
                    What the empty-string case fixes is narrower than this comment first claimed: CSS
                    collapses a leading space at the start of a line, so the city run starts at x 0
                    with the separator present and absent alike. The divergence it closes is between
                    the DOM text (`" Jonesboro, GA 30238"`) and the `title` beside it, which is built
                    with a filter and never carried the space. That is what the tests assert. */}
                {hasStreet ? ' ' : null}
                <span className="text-[13px] font-light text-brand-secondary group-hover:text-brand-green transition-colors">
                  {params.row.cityStateZip}
                </span>
              </>
            ) : null}
          </span>

          {/* Comment Set #1, Pipeline 1.3: row 2 is light. 13/300 on that same node — this row
              carries only beds, baths, sqft and year built (Pipeline.jsx:95), which is exactly the
              set he named. */}
          <span className="mt-[2px] text-[13px] font-light text-brand-secondary truncate">
            {params.row.propertySummary}
          </span>
        </div>
      );
    },
  },
  {
    // 179 = 115 (ceil of the widest option label, "Projects" / "Under Construction", measured
    // live against the real formatOptionLabel markup) + 52 (react-select's own chrome: 20px
    // valueContainer padding, 32px indicatorsContainer/caret) + 12 (the same gutter Strategy and
    // Owner use). 220 was never measured against anything; at exactly 167 (no gutter) the widest
    // label fits with zero slack — the same sub-pixel edge Property was cut on — so the 12px is
    // load-bearing, not decoration.
    field: "stage",
    headerName: "Stage",
    width: 179,
    sortable: false,

    renderCell: (params) => (
      <StageCellEditor
        params={params}
        activeStage={activeStage}
        onStageUpdate={onStageUpdate}
        onSaveOk={onSaveOk}
        onSaveFailed={onSaveFailed}
      />
    ),
  },

  {
    // 119 = 107 (ceil of "Short-Term Rental" at text-[13px], measured in the live app via
    // getBoundingClientRect against real Figtree — "Long-Term Rental" measures 104, both raw
    // STRATEGY_OPTIONS labels beat "Buy & Hold", the longest seed-data passthrough at 65) + 12px
    // right gutter. The span is inline, not a full-width block, so unused width right of the text
    // becomes that gutter on its own — no padding needed on the span itself.
    field: "strategy",
    headerName: "Strategy",
    width: 119,
    sortable: false,

    renderCell: (params) => (
      <span className="text-[13px] text-brand-dark">
        {getStrategyLabel(params.row.raw?.strategy)}
      </span>
    ),
  },

  {
    // The 30px avatar is not the binding constraint — the header label is. "Owner" at the header's
    // own 14px/700 (PipelineGrid.jsx's .MuiDataGrid-columnHeaderTitle sx) measures 44px in the live
    // app, wider than the 30px avatar, and headers get the same "padding: 0 !important" as cells, so
    // an under-sized column leaves the header text sitting right up against "Tags" with almost no
    // gap — measured 6px at width 50 (the avatar's own 30 + 12 gutter, clamped up to MUI's
    // GRID_STRING_COL_DEF default minWidth of 50). 56 = ceil(44) + the same 12px gutter as
    // everywhere else, and clears the 50 floor outright so nothing clamps it.
    field: "owner",
    headerName: "Owner",
    width: 56,
    sortable: false,

    renderCell: (params) => (
      <div
        className="flex items-center justify-center rounded-full text-white"
        style={{ width: 30, height: 30, backgroundColor: '#5F6B5B' }}
      >
        <span style={{ fontSize: 11 }}>
          {getOwnerInitials(params.row.raw?.createdBy, user)}
        </span>
      </div>
    ),
  },

  {
    field: "tagsDisplay",
    headerName: "Tags",
    width: 296,
    sortable: false,

    renderCell: (params) => (
      <TagCellEditor
        params={params}
        allTags={allTags}
        onToggleTag={onToggleTag}
        onManageTags={onManageTags}
      />
    ),
  },

  {
    // qa-verifier finding, 25.08.2026 (fourth pass): 85 closed the sort-icon-chrome fit and the
    // Tags abutment, but left the header text flush at the column's left edge while the value below
    // carries pl-[12px] — a 12px header/value misalignment. Of the three ways to close it against
    // this column's own width, all were correctly rejected (each re-opened either the sort-icon
    // zero-slack edge or the Property/Jonesboro margin). The fix that doesn't touch this column's own
    // slack at all: the pinned column's inherited 64 was never measured either, and its real content
    // is a 12px icon with none of MUI's default button chrome — narrowing it to its own measured need
    // (see the pinned field comment) frees width this column can take without moving Property, whose
    // flex allocation is fixed by the Added+pinned sum, not by either column individually.
    //
    // headerClassName below scopes PipelineGrid.jsx's sx to inset just this column's
    // .MuiDataGrid-columnHeaderTitleContainer by the same 12px the value carries as pl-[12px] — the
    // grid-wide ".MuiDataGrid-cell { padding: 0 !important }" rule sits on .MuiDataGrid-columnHeader
    // itself, one level up from the title container, so it never touches this rule. With that inset:
    // title box = 113 - 12 (inset) - 30 (2px gap + 28px sort-icon container) = 71 against the 43px
    // "Added" needs, 28px slack; value area = 113 - 12 = 101 against 'Invalid Date''s 68.15625px
    // (the row.createdAt-missing case, see the render comment below), 32.8px slack. Both clear the
    // 85px zero-slack floor with room, because 113 is not a value chosen for this column alone — it is
    // 149 (the fixed Added+pinned sum, unchanged since the 85/64 split) minus pinned's measured 36.
    // Verified live post-fix: header scrollWidth === clientWidth at rest, hovered and sorted with the
    // mouse moved away; header-to-value offset 0 (title container's left edge lines up with the date
    // digits' left edge); Tags-pill-to-date gap unchanged at ~11.94px, from the pl-[12px] below, not
    // from this column's own width.
    field: "createdAtDisplay",
    headerName: "Added",
    headerClassName: 'pipeline-added-header',
    width: 113,
    sortable: true,

    // Without a valueGetter the sort comparator reads row.createdAtDisplay (an already-formatted
    // "M/D/YYYY" string) and collates it character by character, so "6/10" sorts ahead of "6/4" —
    // sort on the underlying timestamp instead.
    valueGetter: (_value, row) => {
      const time = new Date(row.raw?.createdAt).getTime();
      return Number.isNaN(time) ? 0 : time;
    },
    // renderCell (below) does the actual on-screen rendering and reads row.createdAtDisplay
    // directly; valueFormatter is kept for the one consumer that bypasses the DOM entirely —
    // Ctrl+C cell copy, wired by useGridClipboard regardless of whether a Toolbar is rendered, which
    // serialises through valueFormatter when one is defined (node_modules/@mui/x-data-grid/hooks/
    // features/clipboard/useGridClipboard.js) — and reuses that exact same string rather than
    // reformatting the timestamp a second time, so the two paths can't drift from each other.
    valueFormatter: (_value, row) => row.createdAtDisplay,

    // No renderCell meant this column fell back to MUI's default cell text (14px,
    // rgba(0, 0, 0, 0.87)) instead of the spec's 13px/#17270D every other column sets explicitly.
    // pl-[12px]: the same 12px counted into the width above — see the field comment. truncate: a row
    // missing createdAt renders the literal "Invalid Date" (68.15625px, measured live at this span's
    // own 13px/400) instead of "--", since Pipeline.jsx calls toLocaleDateString unconditionally with
    // no guard. That clipped mid-word at the old 62px width (no left padding then, so the full 62 was
    // available and 68.16 > 62) — qa-verifier finding, 25.08.2026. At 113 the available width is
    // 101 (113 minus this same pl-[12px]), so 68.16 fits with ~32.8px to spare; truncate stays as a
    // defensive guarantee of an ellipsis rather than a currently-load-bearing fix, since a locale or a
    // future narrowing could make it corrective again. What a missing timestamp should display instead
    // is a product question, not one this width fix decides.
    renderCell: (params) => (
      <span className="text-[13px] text-brand-dark truncate pl-[12px]">{params.row.createdAtDisplay}</span>
    ),
  },

  {
    // qa-verifier finding, 25.08.2026: 64 was inherited from master, unmeasured, exactly the state
    // Stage and Added were both in before their own passes. Measured live: the button carries none of
    // MUI's default chrome (Tailwind's preflight zeroes button padding/border), so its rendered box is
    // exactly its svg's own — getComputedStyle confirmed padding 0, border-width 0 on all sides.
    // headerName is empty, so unlike every text column above there is no header-text constraint to
    // satisfy: the content is the icon alone.
    //
    // **The derivation below is the 25.08 one and its two inputs have both changed since.** It read
    // 12 (icon) + 12 + 12 = 36 off a 12x12 icon in a centred column. Comment Set #1 made the icon
    // 14x14 (Pipeline 3) and right-aligned it behind a 20.5 margin (Pipeline 3.1, the block at
    // `align` below), so today's 36 holds 14 + 20.5 = 34.5 with 1.5 on the left — the same width for
    // different reasons. It is kept because the paragraph that follows it is about `minWidth`, which
    // is still live and still load-bearing; re-deriving the width from the old numbers would give 38
    // and take 19 off Property's flex allocation for nothing.
    //
    // Unlike the left-aligned columns above, where a single 12px gutter buys separation from the next
    // column only, a centered column's declared width splits evenly on both sides of its content. The
    // same 12px gutter convention applied here therefore means 12px of clearance on each side, not one
    // flat +12 that centering would halve to 6 per side: 12 (icon) + 12 + 12 = 36.
    //
    // 36 is below @mui/x-data-grid's own GRID_STRING_COL_DEF default minWidth of 50
    // (colDef/gridStringColDef.js) — confirmed empirically, not just read: dropping this column's width
    // to 20 with no minWidth override rendered at 50 regardless (clamp(width, minWidth, maxWidth) in
    // hooks/features/columns/gridColumnsUtils.js), silently ignoring the declared width. minWidth is
    // therefore not decoration here; deleting it alone reopens the clamp, this column renders 14px
    // wider than declared, and since Added's own width is a separate literal rather than something
    // computed from this one, the 14px comes off Property's flex allocation instead (301 -> 287) —
    // silently, since nothing here re-derives Added from pinned at runtime.
    field: "pinned",
    headerName: "",
    width: 36,
    minWidth: 36,
    sortable: false,
    disableColumnMenu: true,
    // Comment Set #1, Pipeline 3.1: "Updating spacing from table border". The export puts the pin's
    // 14x14 box 20.5 from the table's right edge — clip origin x 2881.5 plus 14 against a row that
    // ends at 2916 — and the centred 36px column was putting it at 12 (browser, 02.09).
    //
    // Right-aligned with the gap declared as a margin rather than widened: the column's own comment
    // above records that Added and pinned share a fixed 149, so growing this one to 55 to centre a
    // 20.5 gap would take 19 straight off Property's flex allocation and reopen the truncation that
    // column has already been fixed for twice. The margin leaves 1.5 on the left, and it holds the
    // 20.5 whatever the column width becomes later.
    align: 'right',
    headerAlign: 'center',

    renderCell: ({ row }) => (
      <button
        onClick={(e) => {
          e.stopPropagation()
          // The save itself (PATCH, generation gate, rollback on failure) lives in Pipeline.jsx's
          // handlePinToggle -- this factory is a plain function called inside a useMemo, not a
          // component, so it cannot hold the ref-based state that discipline needs. See
          // src/pages/README.md, "Background-save race".
          onPinToggle?.(row.id, !row.pinned)
        }}
        style={{ marginRight: 20.5 }}
        className="flex items-center justify-center hover:opacity-70 transition-opacity"
        title={row.pinned ? 'Unpin' : 'Pin to top'}
      >
        <PipelinePinIcon active={row.pinned} />
      </button>
    ),
  },
];
